import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, getJwt } from '../services/AuthService';
import { authEvents } from '../services/AxiosConfig';
import { logUserLogin } from '../services/ActivityLogService';
import packageJson from '../../package.json';

export default function LoginForm({ onLoginSuccess = () => {} }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  // Monitorar eventos de autenticação
  useEffect(() => {
    const handleLoginEvent = () => {
      // Chamar callback opcional passado como prop
      onLoginSuccess();

      // Redirecionar para a página principal após login bem-sucedido
      navigate('/map');
    };

    globalThis.addEventListener(authEvents.login.type, handleLoginEvent);

    return () => {
      globalThis.removeEventListener(authEvents.login.type, handleLoginEvent);
    };
  }, [onLoginSuccess, navigate]);

  // Validar formulário
  const validateForm = () => {
    const errors = {};

    if (!email) {
      errors.email = 'Email é obrigatório';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      errors.email = 'Email inválido';
    }

    if (!password) {
      errors.password = 'Senha é obrigatória';
    } else if (password.length < 6) {
      errors.password = 'Senha deve ter pelo menos 6 caracteres';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validar formulário antes de enviar
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await login(email, password);
      // Obter o token e dados do usuário
      getJwt();
      // Registrar o login bem-sucedido, se temos os dados necessários
      if (response?.client_id) {
        try {
          await logUserLogin(response.client_id, response.client_email || email);
        } catch (logError) {
          // Não interrompe o fluxo em caso de erro ao registrar atividade
          console.error('Erro ao registrar atividade de login:', logError);
        }
      }
    } catch (err) {
      console.error('Erro de login:', err);

      if (err.response) {
        // Erro retornado pelo servidor
        if (err.response.status === 401) {
          setError('Credenciais inválidas. Verifique seu email e senha.');
        } else if (err.response.data?.message) {
          setError(err.response.data.message);
        } else {
          setError(`Erro ${err.response.status}: Falha na autenticação`);
        }
      } else if (err.request) {
        // Erro de conexão
        setError('Não foi possível conectar ao servidor. Verifique sua conexão.');
      } else {
        // Outros erros
        setError('Erro ao processar login: ' + (err.message || 'Erro desconhecido'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={3} sx={{ p: 4, minWidth: 320, width: '100%', maxWidth: 420 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <img src="/logo.png" alt="Green Nexus" style={{ height: '64px', width: 'auto' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Green Nexus
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Plataforma Green:Nexus
          </Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            required
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            sx={{ mb: 2 }}
            autoFocus
            autoComplete="email"
          />
          <TextField
            label="Senha"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            sx={{ mb: 2 }}
            autoComplete="current-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Entrar'}
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button variant="text" size="small" onClick={() => navigate('/forgot-password')}>
              Esqueci a senha
            </Button>
            <Button variant="text" size="small" onClick={() => navigate('/register')}>
              Criar conta
            </Button>
          </Box>

          {/* Footer com informações de versão e build */}
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid #e0e0e0',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              v{packageJson.version}
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}

LoginForm.propTypes = {
  onLoginSuccess: PropTypes.func,
};
